import { useRef, useEffect } from "react";
import { renderDate } from "@intrasync/utils";
import { ScheduleGridBodyCellProps } from "components/ScheduleGrid/ScheduleGrid.Types";
import injectStores from "components/RootStore/injectStores";
import RootStore from "stores/RootStore";
import DepartmentProductionScheduleStore from "../DepartmentProductionScheduleStore";

interface DepartmentProductionScheduleLeftBodyCellProps
  extends ScheduleGridBodyCellProps {
  scheduleDate: string;
  rowCount: number;
  rowData: { entities: { moldNumber: string }[] };
  rootStore?: RootStore;
}

const DepartmentProductionScheduleLeftBodyCell = injectStores(
  (props: DepartmentProductionScheduleLeftBodyCellProps) => {
    const { rowCount, scheduleDate, rootStore } = props;
    const cellRef = useRef<HTMLDivElement>(null);

    const departmentProductionScheduleStore =
      rootStore?.departmentProductionScheduleStore as DepartmentProductionScheduleStore;

    // Register this cell as a drop zone
    useEffect(() => {
      const updateDropZone = () => {
        if (cellRef.current && departmentProductionScheduleStore) {
          const rect = cellRef.current.getBoundingClientRect();
          departmentProductionScheduleStore.registerDropZone(scheduleDate, rect);
        }
      };

      // Initial registration
      updateDropZone();

      // Update on scroll/resize
      window.addEventListener("scroll", updateDropZone, true);
      window.addEventListener("resize", updateDropZone);

      return () => {
        departmentProductionScheduleStore?.unregisterDropZone(scheduleDate);
        window.removeEventListener("scroll", updateDropZone, true);
        window.removeEventListener("resize", updateDropZone);
      };
    }, [scheduleDate, departmentProductionScheduleStore]);

    const isDropTarget = departmentProductionScheduleStore?.dropTargetDate === scheduleDate;
    const isDragging = departmentProductionScheduleStore?.draggedEntityId !== null;

    const defaultBgColor = props.rowIndex % 2 === 0 ? "#f0f0f0" : "#e0e0e0";
    const backgroundColor = isDropTarget ? "#bbdefb" : defaultBgColor;
    const borderStyle = isDropTarget ? "2px dashed #2196f3" : "1px solid #eee";

    return (
      <div
        ref={cellRef}
        style={{
          ...props.style,
          ...props.styles,
          borderLeft: borderStyle,
          borderRight: borderStyle,
          borderTop: borderStyle,
          borderBottom: isDropTarget ? "2px dashed #2196f3" : "2px solid black",
          backgroundColor: backgroundColor,
          height: ((props?.style?.height as number) || 50) - 3,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          padding: "4px 8px",
          transition: isDragging ? "background-color 0.15s, border 0.15s" : "none",
        }}
      >
        <b style={{ fontSize: "13px", marginBottom: "2px" }}>
          {renderDate(scheduleDate)}
        </b>
        {rowCount >= 1 && (
          <span style={{ fontSize: "12px", color: "#555" }}>
            {rowCount} {rowCount === 1 ? "piece" : "pieces"}
          </span>
        )}
        {isDropTarget && (
          <span
            style={{
              fontSize: "11px",
              color: "#1976d2",
              fontWeight: 600,
              marginTop: "2px",
            }}
          >
            Drop here
          </span>
        )}
      </div>
    );
  }
);

export default DepartmentProductionScheduleLeftBodyCell;
